import { stateWords } from '@/ui/words';
import { useState } from 'react';
import { Link } from 'react-router';
import { failureOf } from '@/api/errors';
import { useConfirmTwinTask, useTwinHandoff } from '@/api/hooks';
import type { WireTwinPlanAnswer } from '@/api/wire';
import { Badge, Button, Kicker } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';
import { ToolLabel, useToolNames } from '@/app/technical';
import { recordLine, TOOL_STATUS, VIA } from '@/tasks/copy';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { explainHandoff, explainTwinPlan } from './explain';

type Plan = Extract<WireTwinPlanAnswer, { kind: 'plan' }>;
type Handoff = Extract<WireTwinPlanAnswer, { kind: 'handoff' }>;

/**
 * The key a confirmation names (`^[A-Za-z0-9_-]{8,80}$`), made once per plan
 * in this browser. A second press, or a retry after an answer that never
 * arrived, sends the same one, so the platform makes one task of it.
 */
function idempotencyKey(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/**
 * A twin's plan, read before anything runs (Amendment 0011, ADR-0139): what
 * it is for, every word that goes to the public web search, and each tool
 * with where it stands. Nothing here is the model's prose about the plan —
 * it is the plan the platform stored, whose digest the confirmation names.
 */
export function TwinPlanCard({
  answer,
  twin,
  changing,
  onChange,
  onMove,
}: {
  answer: Plan;
  twin: string;
  changing: boolean;
  /** Ask again, superseding this plan, with what the person typed. */
  onChange: () => void;
  /** Put the task in an available department agent's composer for the person's review. */
  onMove: { name: string; act: () => void } | undefined;
}) {
  const names = useToolNames();
  const confirm = useConfirmTwinTask();
  const who = useWho();
  const [key] = useState(idempotencyKey);
  const { plan, inventory } = answer;
  const purpose = new Map(plan.steps.map((s) => [s.toolId, s.purpose]));
  const confirmed = confirm.data;
  return (
    <li className="flex flex-col gap-3 border border-strong bg-page p-4" data-testid="twin-plan" aria-label={`Plan: ${plan.title}`}>
      <div className="flex flex-col gap-1">
        <Kicker>{confirmed ? `Plan ${answer.version} · confirmed` : `Plan ${answer.version} · nothing has run`}</Kicker>
        <h3 className="m-0 text-body font-semibold">{plan.title}</h3>
        <p className="m-0 text-dense text-secondary">{plan.subject}</p>
        {plan.originalRequest && <details><summary>Complete original request</summary><pre className="whitespace-pre-wrap break-words">{plan.originalRequest}</pre></details>}
        <p className="m-0 text-dense">
          <span className="font-semibold">You get back: </span>
          {plan.output}
        </p>
      </div>

      <Explainer label="Who asked, who decides and what happens next" context={explainTwinPlan(answer, { twin, confirmed: confirmed !== undefined }, who)} />

      <section className="flex flex-col gap-1.5" aria-label="Task materials">
        <span className="text-dense font-semibold">Material this task may read:</span>
        {(plan.materials ?? []).length > 0 ? (
          <ul className="m-0 list-disc pl-5 text-dense">
            {plan.materials!.map((item) => <li key={item.id}>{item.filename} · {stateWords(item.source).toLowerCase()}</li>)}
          </ul>
        ) : (
          <span className="text-dense text-secondary">No document attached to this plan.</span>
        )}
      </section>

      <section className="flex flex-col gap-1.5" aria-label="Web searches" data-testid="twin-plan-queries">
        {plan.queries.length > 0 ? (
          <>
            <span className="text-dense font-semibold">These exact words go to the public web search:</span>
            <ol className="m-0 flex list-none flex-col gap-1 p-0">
              {plan.queries.map((q) => (
                <li key={q} className="border border-control bg-raised px-2.5 py-1.5 font-mono text-dense [overflow-wrap:anywhere]">
                  {q}
                </li>
              ))}
            </ol>
          </>
        ) : (
          <span className="text-dense text-secondary">No web search: nothing from this task goes to the public web.</span>
        )}
        {answer.refusedQueries > 0 && (
          <span className="text-dense text-meta">
            {answer.refusedQueries === 1 ? 'One search was' : `${answer.refusedQueries} searches were`} left out: they carried words that stay inside the platform.
          </span>
        )}
      </section>

      <section className="flex flex-col gap-1.5" aria-label="Tools">
        <span className="text-dense font-semibold">
          The tools, at most {plan.maxCalls} {plan.maxCalls === 1 ? 'call' : 'calls'} in all:
        </span>
        <ul className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0">
          {inventory.map((t) => (
            <li key={t.toolId} className="flex flex-col gap-1 bg-page px-3 py-2 text-dense" data-testid="twin-plan-tool" data-status={t.status}>
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="text-dense font-semibold [overflow-wrap:anywhere]"><ToolLabel id={t.toolId} /></span>
                <Badge tone={TOOL_STATUS[t.status].tone}>{TOOL_STATUS[t.status].label}</Badge>
                {t.via && <span className="text-meta">{VIA[t.via]}</span>}
              </span>
              <span className="text-secondary">{purpose.get(t.toolId) ?? t.description}</span>
              {t.remedy && <span>{t.remedy}</span>}
              {t.record && <span className="text-meta">{recordLine(t.record, t.record.needed)}</span>}
            </li>
          ))}
        </ul>
        {answer.dropped.length > 0 && (
          <span className="text-dense text-meta" data-testid="twin-plan-dropped">
            Left out: tools the library does not have — {names(answer.dropped)}.
          </span>
        )}
      </section>

      {confirmed ? (
        <div className="flex flex-col gap-1.5 border-l-2 border-accent pl-3 text-dense" data-testid="twin-task-outcome">
          {confirmed.replayed && <span className="text-meta">This plan was confirmed already; it is the same task.</span>}
          {confirmed.state === 'waiting' ? (
            <span>
              Waiting on {names(confirmed.waitingOn)}. A superadmin has been told; {twin} starts the moment it is granted.
            </span>
          ) : (
            <span>Started. {twin} tells you when it is done.</span>
          )}
          {confirmed.earned.length > 0 && <span className="text-secondary">Granted on its record, with nobody’s press: {names(confirmed.earned)}.</span>}
          <Link to={`/tasks/${confirmed.taskId}`} className="w-fit text-accent underline underline-offset-[3px]">
            Open the task
          </Link>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="row"
            variant="primary"
            disabled={confirm.isPending || changing}
            onClick={() => confirm.mutate({ planId: answer.planId, planHash: answer.planHash, key })}
            title="Runs exactly this plan: these tools, these searches, and nothing else."
          >
            Confirm task
          </Button>
          <Button size="row" variant="secondary" disabled={confirm.isPending || changing} onClick={onChange} title="Type what to change first; the twin writes a new version of the plan.">
            Change the plan
          </Button>
          {onMove && (
            <Button size="row" variant="secondary" disabled={confirm.isPending || changing} onClick={onMove.act}>
              Ask {onMove.name} instead
            </Button>
          )}
          <span className="text-label text-meta">To change it, type what is different, then press Change the plan.</span>
        </div>
      )}
      {confirm.isError && <Refusal compact failure={failureOf(confirm.error)} />}
    </li>
  );
}

/**
 * A company the platform holds as unapproved is not researched by a twin:
 * it is asked of Nia in Executive, as a request, with the person's words
 * sealed as its body (§18, ADR-0064). The person presses for that.
 */
export function HandoffCard({ answer, ask }: { answer: Handoff; ask: string }) {
  const handoff = useTwinHandoff();
  const who = useWho();
  const done = handoff.data;
  return (
    <li className="flex flex-col gap-2 border-l-2 border-warning bg-page px-3 py-2.5" data-testid="twin-handoff">
      <Kicker>Not mine to plan</Kicker>
      <span className="text-dense">{answer.why}</span>
      <Explainer context={explainHandoff(answer, who, done !== undefined)} />
      {done ? (
        <span className="text-dense text-secondary" data-testid="twin-handoff-done">
          Asked {done.to}.{' '}
          {done.requestId && (
            <Link to={`/requests/${done.requestId}`} className="text-accent underline underline-offset-[3px]">
              Open the request
            </Link>
          )}
          {!done.requestId && done.taskId && 'It is their task now.'}
        </span>
      ) : (
        <div>
          <Button icon="ask" size="row" variant="primary" disabled={handoff.isPending} onClick={() => handoff.mutate({ ask })}>Ask Nia</Button>
        </div>
      )}
      {handoff.isError && <Refusal compact failure={failureOf(handoff.error)} />}
    </li>
  );
}
